import type { DiningTable, Product } from '@/types'
import type { OfflineOrder } from '@/lib/offline/offlineStore'
import type { ModifierOptionPrice } from '@/lib/lineModifiers'
import { restoreTicketLines, type PersistedTicketLine } from './useServerTicketDraft'
import type { KOTItem } from './KOTServerInterface'

/**
 * The floor's view of tickets held on this tablet (F-FE-N125).
 *
 * A held ticket has never reached the server, so GET /tables — which draws
 * the floor map — knows nothing about it. Left alone, the next waiter would
 * see that table as free and seat a fresh party on top of a ticket the
 * kitchen is already cooking. These helpers overlay the local queue onto the
 * cached floor, and rebuild a held ticket's lines from the menu so it can be
 * reopened and appended to.
 *
 * Pure functions, so the rules are testable without a screen.
 */

/** Prefix of the synthetic order id a held table carries on the map. */
export const HELD_ORDER_ID_PREFIX = 'offline:'

/** The pending CREATE held for a table on this tablet, if any. */
export function heldEntryForTable(
  entries: ReadonlyArray<OfflineOrder>,
  tableId: string | null | undefined,
): OfflineOrder | null {
  if (!tableId) return null
  return (
    entries.find((o) => o.status === 'pending' && o.kind === 'create' && o.tableId === tableId) ??
    null
  )
}

/**
 * Overlay held tickets onto the cached floor.
 *
 * A table the server already reports occupied is returned as-is — the server's
 * summary wins, and an append entry rides on that order anyway. A free table
 * with a held create becomes occupied here, on this tablet only, carrying the
 * local ticket ref where the order number goes. `item_count` is the summed
 * quantity so the tile never renders in the "seated · no order" tint, and
 * `total_amount` is 0 because no figure this tablet holds is a bill.
 */
export function mergeHeldTables(
  tables: ReadonlyArray<DiningTable>,
  entries: ReadonlyArray<OfflineOrder>,
): DiningTable[] {
  return tables.map((t) => {
    if (t.has_active_order ?? t.is_occupied) return t
    const held = heldEntryForTable(entries, t.id)
    if (!held) return t
    return {
      ...t,
      is_occupied: true,
      has_active_order: true,
      current_order: {
        id: HELD_ORDER_ID_PREFIX + held.opId,
        order_number: held.ticketRef || held.orderNumber || '',
        status: 'offline',
        created_at: new Date(held.capturedAt).toISOString(),
        total_amount: 0,
        guest_count: held.guestCount ?? 0,
        is_open_tab: false,
        item_count: held.lines.reduce((n, l) => n + l.quantity, 0),
      },
    }
  })
}

/**
 * Rebuild a held ticket's lines for the sidebar, as SENT.
 *
 * Goes through `restoreTicketLines` — the same re-pricing and folding a
 * restored draft gets — so a held line is priced from the current menu, never
 * from anything cached with the ticket. A product that left the menu is
 * counted, not invented. Ids carry a `held-` prefix: `refreshOrder` keys local
 * drafts on `draft-`, and a held line is not a draft.
 *
 * The queue stores option ids only (invariant #11), while a sidebar line
 * carries the option's group as well; the group is looked up in the catalogue
 * here, and an option the catalogue no longer knows is dropped by
 * `restoreTicketLines` exactly as it is for a restored draft.
 */
export function heldEntryToKotItems(
  entry: OfflineOrder,
  products: ReadonlyArray<Product>,
  priceIndexFor: (productId: string) => ReadonlyMap<string, ModifierOptionPrice>,
  optionByIdFor: (productId: string) => ReadonlyMap<string, { group_id: string }>,
): { items: KOTItem[]; dropped: number } {
  const lines: PersistedTicketLine[] = entry.lines.map((l) => ({
    productId: l.productId,
    quantity: l.quantity,
    ...(l.special_instructions ? { special_instructions: l.special_instructions } : {}),
    ...(l.selections && l.selections.length > 0
      ? {
          selections: l.selections.map((s) => ({
            group_id: optionByIdFor(l.productId).get(s.option_id)?.group_id ?? '',
            option_id: s.option_id,
            quantity: s.quantity,
          })),
        }
      : {}),
  }))
  let n = 0
  const { items, dropped } = restoreTicketLines(
    lines,
    [...products],
    priceIndexFor,
    () => `held-${entry.opId}-${n++}`,
  )
  return { items: items.map((i) => ({ ...i, status: 'sent' as const })), dropped }
}
